"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ArgCharCompact = void 0;
const tslib_1 = require("tslib");
const React = tslib_1.__importStar(require("react"));
const use_t_1 = require("use-t");
const nano_theme_1 = require("nano-theme");
const ContextItem_1 = require("../../ContextItem");
const Input_1 = require("../../../../2-inline-block/Input");
const BasicButton_1 = require("../../../../2-inline-block/BasicButton");
const Popup_1 = require("../../../Popup");
const context_1 = require("../../../Popup/context");
const ClickAway_1 = require("../../../../utils/ClickAway");
const context_2 = require("../../../../styles/context");
const emoji_picker_react_1 = tslib_1.__importStar(require("emoji-picker-react"));
const OptionalBadge_1 = require("./OptionalBadge");
const DefaultableToggle_1 = require("./DefaultableToggle");
const AutoValue_1 = require("./AutoValue");
const stopPointer = (e) => {
    e.stopPropagation();
};
const preventPopupMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
};
const buttonInnerClass = (0, nano_theme_1.rule)({
    d: 'inline-flex',
    ai: 'center',
    jc: 'center',
    fz: '17px',
    lh: 1,
    minW: '20px',
});
const takeLastCodePoints = (text, n) => {
    if (!text || n <= 0)
        return '';
    const points = Array.from(text);
    return points.slice(-n).join('');
};
const readStructured = (v) => {
    if (v && typeof v === 'object' && 'def' in v) {
        const s = v;
        return { def: !!s.def, value: String(s.value ?? '') };
    }
    return { def: false, value: typeof v === 'string' ? v : '' };
};
const EmojiPickerInner = ({ light, onSelect }) => {
    const popup = (0, context_1.usePopup)();
    const handleClickAway = React.useCallback(() => popup?.close(), [popup]);
    return (React.createElement(ClickAway_1.ClickAway, { onMouseDown: stopPointer, onMouseUp: stopPointer, onClick: stopPointer, onClickAway: handleClickAway },
        React.createElement(emoji_picker_react_1.default, { theme: light ? emoji_picker_react_1.Theme.LIGHT : emoji_picker_react_1.Theme.DARK, height: 420, width: 400, skinTonePickerLocation: emoji_picker_react_1.SkinTonePickerLocation.PREVIEW, onEmojiClick: (data) => {
                onSelect(data.emoji);
                popup?.close();
            } })));
};
const ArgCharCompact = ({ param, value, onChange, focus }) => {
    const [t] = (0, use_t_1.useT)();
    const styles = (0, context_2.useStyles)();
    const label = param.display?.() ?? t(param.name ?? param.id ?? '');
    const emojiOnly = param.emoji === 'only';
    const emojiEnabled = !!param.emoji;
    const showInput = !emojiOnly;
    const showButton = emojiEnabled;
    const maxLen = param.length ?? (emojiEnabled ? 2 : 1);
    const defaultable = !!param.defaultable;
    const s = readStructured(value);
    const def = defaultable && s.def;
    const emit = (next) => {
        if (defaultable)
            onChange?.(next);
        else
            onChange?.(next.value);
    };
    const setText = (v) => emit({ def: false, value: v });
    const enterCustom = () => emit({ def: false, value: s.value });
    const revertToAuto = () => emit({ def: true, value: s.value });
    // biome-ignore lint/correctness/useExhaustiveDependencies: setText/emit closure intentionally pinned by listed deps
    const handleTextChange = React.useCallback((text) => {
        setText(takeLastCodePoints(text, maxLen));
    }, [maxLen, onChange, defaultable, s.value]);
    // biome-ignore lint/correctness/useExhaustiveDependencies: setText/emit closure intentionally pinned by listed deps
    const handlePick = React.useCallback((picked) => setText(picked), [onChange, defaultable, s.value]);
    const handleFocus = React.useCallback((event) => {
        const input = event.target;
        requestAnimationFrame(() => {
            try {
                input.select();
            }
            catch { }
        });
    }, []);
    const renderEmojiPicker = React.useCallback(() => React.createElement(EmojiPickerInner, { light: !!styles.light, onSelect: handlePick }), [handlePick, styles.light]);
    const inputWidth = showButton ? Math.max(44, 32 + maxLen * 10) : Math.max(56, 44 + maxLen * 10);
    const customControl = (React.createElement("span", { style: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: showInput && showButton ? 4 : 0,
            margin: '-5px -8px -5px 0',
        } },
        showInput && (React.createElement("span", { style: { width: inputWidth } },
            React.createElement(Input_1.Input, { size: -3, type: "text", value: s.value, placeholder: param.placeholder ?? '', focus: focus, align: "center", onChange: handleTextChange, onFocus: handleFocus }))),
        showButton && (React.createElement(Popup_1.Popup, { renderContext: renderEmojiPicker },
            React.createElement(BasicButton_1.BasicButton, { type: "button", fill: true, width: 36, height: 28, rounder: true, onMouseDown: preventPopupMouseDown },
                React.createElement("span", { className: buttonInnerClass }, s.value || param.placeholder || '🙂'))))));
    const autoDisplay = (React.createElement(AutoValue_1.AutoValue, { onClick: enterCustom },
        React.createElement("span", { style: { fontSize: 13 } }, t('auto'))));
    const right = defaultable ? (React.createElement("span", { style: { display: 'inline-flex', alignItems: 'center', gap: 4, margin: '-5px -4px -5px 0' } }, def ? (React.createElement(React.Fragment, null,
        React.createElement(DefaultableToggle_1.DefaultableToggle, { def: true, onClick: enterCustom }),
        autoDisplay)) : (React.createElement(React.Fragment, null,
        React.createElement(DefaultableToggle_1.DefaultableToggle, { def: false, onClick: revertToAuto }),
        customControl)))) : (customControl);
    return (React.createElement(ContextItem_1.ContextItem, { icon: param.icon?.(), control: true, inset: true, right: right },
        React.createElement("span", null,
            label,
            param.optional && React.createElement(OptionalBadge_1.OptionalBadge, null))));
};
exports.ArgCharCompact = ArgCharCompact;
